import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useSEO } from "@/hooks/useSEO";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import {
  ChevronDown, ChevronUp, ArrowRight, Check, X, FileCode2,
  Inbox, Send, ShieldCheck, CalendarClock,
} from "lucide-react";

function FAQItem({ question, answer }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border-b border-gray-200 last:border-0">
      <button
        onClick={() => setOpen(!open)}
        style={{ display: "flex" }} className="w-full flex items-center justify-between py-4 text-left"
        data-testid={`faq-toggle-${question.slice(0, 20).replace(/\s/g, '-')}`}
      >
        <span className="font-medium text-gray-900 pr-4">{question}</span>
        {open ? <ChevronUp className="h-5 w-5 text-gray-400 flex-shrink-0" /> : <ChevronDown className="h-5 w-5 text-gray-400 flex-shrink-0" />}
      </button>
      <p className={`text-gray-600 pb-4 text-sm leading-relaxed ${open ? "block" : "hidden"}`}>{answer}</p>
    </div>
  );
}

export default function ERechnungFreelancer() {
  const navigate = useNavigate();

  useSEO({
    title: "E-Rechnung für Freelancer: XRechnung & ZUGFeRD einfach erklärt | ivoliq",
    description: "E-Rechnungspflicht 2025–2028: Was Freelancer jetzt tun müssen. ivoliq erstellt ZUGFeRD- und XRechnungen automatisch und empfängt eingehende E-Rechnungen per Mail. Kostenlos starten.",
    canonical: "https://ivoliq.io/e-rechnung-freelancer",
  });

  const timeline = [
    { date: "Seit 1.1.2025", title: "Empfangspflicht (DE)", desc: "Jedes B2B-Unternehmen — auch jeder Freelancer — muss E-Rechnungen empfangen können. Ein E-Mail-Postfach genügt, aber du musst die Daten auch verarbeiten können." },
    { date: "Ab 1.1.2027", title: "Sendepflicht für Umsätze > 800.000 €", desc: "Größere Unternehmen müssen B2B-Rechnungen als E-Rechnung ausstellen. Ein reines PDF zählt dann nicht mehr als Rechnung." },
    { date: "Ab 1.1.2028", title: "Sendepflicht für alle (DE)", desc: "Auch Freelancer müssen B2B-Rechnungen als E-Rechnung senden. Ausnahmen: Kleinunternehmer (§ 19 UStG) und Kleinbeträge bis 250 €." },
  ];

  const painPoints = [
    { pain: "Extra-Tool oder Portal für XRechnungen", fix: "XRechnung-Export direkt an jeder Rechnung" },
    { pain: "PDF und XML getrennt verwalten", fix: "Jede PDF-Rechnung trägt das ZUGFeRD-XML bereits in sich" },
    { pain: "Eingehende XML-Rechnungen sind unlesbar", fix: "Per Mail an deine Beleg-Adresse — Beträge & USt werden exakt übernommen" },
    { pain: "Angst vor Formatfehlern (EN 16931)", fix: "Automatische Validierung bei jeder Erstellung" },
    { pain: "Leitweg-ID? Steuerkategorie? Keine Ahnung", fix: "Geführte Felder — ivoliq setzt die Pflichtangaben korrekt" },
  ];

  const features = [
    { icon: Send, title: "ZUGFeRD automatisch beim Senden", desc: "Jede ivoliq-PDF-Rechnung (DE/AT, EUR) enthält das eingebettete Rechnungs-XML im Factur-X-Format (Profil EN 16931). Deine Rechnung sieht aus wie immer — und gilt trotzdem als vollwertige E-Rechnung. Ohne Extra-Klick." },
    { icon: FileCode2, title: "XRechnung für Behörden (B2G)", desc: "Aufträge von Bund, Ländern oder Kommunen? Mit einem Klick exportierst du die XRechnung 3.0 inklusive Leitweg-ID — geprüft gegen die EN-16931-Geschäftsregeln." },
    { icon: Inbox, title: "E-Rechnungen empfangen & verstehen", desc: "Leite eingehende XRechnungen oder ZUGFeRD-PDFs an deine persönliche Beleg-Mail-Adresse weiter: ivoliq liest Betrag, USt, Lieferant und Rechnungsnummer exakt aus dem XML — keine KI-Schätzung, keine Tippfehler." },
    { icon: ShieldCheck, title: "Rechtssicher in DE, AT & CH", desc: "Pflichtangaben nach § 14 UStG (DE) und § 11 UStG (AT), Kleinunternehmer- und Reverse-Charge-Szenarien, Swiss QR-Bill für die Schweiz — alles in einem Tool." },
  ];

  const faqItems = [
    { q: "Was ist eine E-Rechnung — reicht ein PDF nicht?", a: "Eine E-Rechnung ist ein strukturierter, maschinenlesbarer Datensatz nach der EU-Norm EN 16931 — z. B. XRechnung (reines XML) oder ZUGFeRD (PDF mit eingebettetem XML). Ein gewöhnliches PDF ist ab den Übergangsfristen (2027/2028) im deutschen B2B-Geschäft keine gültige Rechnung mehr." },
    { q: "Was ist der Unterschied zwischen XRechnung und ZUGFeRD?", a: "XRechnung ist eine reine XML-Datei — Standard für Rechnungen an deutsche Behörden (B2G). ZUGFeRD ist ein Hybrid: ein normales PDF, in dem dieselben Daten als XML eingebettet sind. Menschen lesen das PDF, Buchhaltungssysteme das XML. Für B2B ist ZUGFeRD meist die praktischste Wahl — ivoliq erstellt beides." },
    { q: "Gilt die E-Rechnungspflicht auch für Kleinunternehmer?", a: "Kleinunternehmer nach § 19 UStG sind von der Pflicht zum Ausstellen dauerhaft ausgenommen. Empfangen können müssen sie E-Rechnungen aber trotzdem — auch dafür genügt die ivoliq Beleg-Mail-Adresse." },
    { q: "Wie erfülle ich die Empfangspflicht seit 2025?", a: "Du musst E-Rechnungen annehmen und verarbeiten können. Mit ivoliq leitest du sie einfach an deine persönliche Beleg-Mail-Adresse weiter: Die Daten werden exakt aus dem XML übernommen und als Ausgabe verbucht — inklusive Vorsteuer für die Umsatzsteuervoranmeldung." },
    { q: "Was ist mit Österreich und der Schweiz?", a: "In Österreich gibt es keine allgemeine B2B-Pflicht; nur Rechnungen an den Bund brauchen ein strukturiertes Format. In der Schweiz gilt keine E-Rechnungspflicht — dort ist die QR-Rechnung der Standard, die ivoliq vollständig unterstützt. ivoliq-Rechnungen aus AT erhalten das ZUGFeRD-XML trotzdem automatisch: Deine deutschen Kunden können sie ab 2027 problemlos verarbeiten." },
    { q: "Brauche ich Peppol?", a: "Nein. Die deutsche E-Rechnungspflicht schreibt kein Übertragungsnetzwerk vor — der Versand per E-Mail genügt. Peppol ist ein optionales Netzwerk für den automatisierten Austausch, das vor allem Konzerne und Behörden nutzen." },
    { q: "Kostet die E-Rechnung in ivoliq extra?", a: "Nein. ZUGFeRD-Einbettung, XRechnung-Export und der E-Rechnungs-Empfang über die Beleg-Mail-Adresse sind Teil der normalen Rechnungsfunktion." },
  ];

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqItems.map((item) => ({
      "@type": "Question",
      name: item.q,
      acceptedAnswer: { "@type": "Answer", text: item.a },
    })),
  };

  return (
    <div className="min-h-screen bg-white">
      <header className="sticky top-0 z-50 bg-white/95 backdrop-blur border-b border-gray-100">
        <div className="max-w-6xl mx-auto px-4 h-16 flex items-center justify-between">
          <Link to="/" className="text-xl font-bold text-gray-900" data-testid="header-logo">ivoliq</Link>
          <div className="flex items-center gap-3">
            <Link to="/login" className="text-sm text-gray-600 hover:text-gray-900">Anmelden</Link>
            <Button size="sm" onClick={() => navigate("/signup")} className="bg-emerald-600 hover:bg-emerald-700" data-testid="header-cta">
              Kostenlos starten
            </Button>
          </div>
        </div>
      </header>

      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }} />

      {/* HERO */}
      <section className="pt-24 pb-16 bg-gradient-to-b from-emerald-50 to-white" data-testid="hero-section">
        <div className="max-w-6xl mx-auto px-4 text-center">
          <div className="inline-flex items-center gap-2 bg-emerald-100 text-emerald-700 px-4 py-1.5 rounded-full text-sm font-semibold mb-6">
            <FileCode2 className="h-4 w-4" /> XRechnung & ZUGFeRD
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 leading-tight">
            E-Rechnung für Freelancer:<br />
            <span className="text-emerald-600">Pflicht erfüllt, ohne Aufwand</span>
          </h1>
          <p className="text-lg text-gray-600 mt-6 max-w-2xl mx-auto leading-relaxed">
            Seit 2025 musst du E-Rechnungen empfangen, ab 2027/2028 auch senden.
            ivoliq macht beides automatisch: Jede PDF-Rechnung trägt das ZUGFeRD-XML in sich,
            XRechnungen exportierst du per Klick — und eingehende E-Rechnungen werden exakt verbucht.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center mt-8">
            <Button size="lg" onClick={() => navigate("/signup")} className="bg-emerald-600 hover:bg-emerald-700 text-white px-8" data-testid="hero-cta-register">
              Kostenlos starten <ArrowRight className="h-5 w-5 ml-2" />
            </Button>
            <Button size="lg" variant="outline" onClick={() => navigate("/rechnungsvorlage-freelancer")} data-testid="hero-cta-invoicing">
              <FileCode2 className="h-5 w-5 mr-2" /> Zur Rechnungserstellung
            </Button>
          </div>
          <p className="text-sm text-gray-500 mt-4">
            EN 16931 konform &middot; ZUGFeRD, XRechnung &amp; Empfang inklusive &middot; Free Plan für immer
          </p>
        </div>
      </section>

      {/* TIMELINE */}
      <section className="py-16 bg-white" data-testid="timeline-section">
        <div className="max-w-4xl mx-auto px-4">
          <h2 className="text-base sm:text-lg font-bold text-gray-900 text-center mb-4">
            Die E-Rechnungspflicht in Deutschland: der Zeitplan
          </h2>
          <p className="text-gray-600 text-center mb-10 max-w-2xl mx-auto text-sm leading-relaxed">
            Das Wachstumschancengesetz führt die E-Rechnung im B2B-Geschäft stufenweise ein.
            Diese drei Daten solltest du kennen:
          </p>
          <div className="grid md:grid-cols-3 gap-6">
            {timeline.map((t) => (
              <div key={t.date} className="bg-gray-50 rounded-xl border p-6" data-testid={`timeline-${t.date.replace(/\W/g, '')}`}>
                <div className="inline-flex items-center gap-2 text-emerald-700 font-semibold text-sm mb-2">
                  <CalendarClock className="h-4 w-4" /> {t.date}
                </div>
                <h3 className="font-semibold text-gray-900 mb-2">{t.title}</h3>
                <p className="text-sm text-gray-600 leading-relaxed">{t.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PAIN → FIX */}
      <section className="py-16 bg-gray-50" data-testid="painpoints-section">
        <div className="max-w-4xl mx-auto px-4">
          <h2 className="text-base sm:text-lg font-bold text-gray-900 text-center mb-10">
            E-Rechnung ohne ivoliq vs. mit ivoliq
          </h2>
          <div className="bg-white rounded-xl shadow-sm border overflow-x-auto">
            <table className="w-full min-w-[560px]">
              <thead>
                <tr className="bg-gray-50 border-b">
                  <th className="text-left px-4 py-3 text-sm font-medium text-gray-600">
                    <span className="inline-flex items-center gap-2"><X className="h-4 w-4 text-red-500" /> Ohne ivoliq</span>
                  </th>
                  <th className="text-left px-4 py-3 text-sm font-medium text-emerald-700 bg-emerald-50">
                    <span className="inline-flex items-center gap-2"><Check className="h-4 w-4" /> Mit ivoliq</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {painPoints.map((p, i) => (
                  <tr key={i} className="border-b last:border-0">
                    <td className="px-4 py-3 text-sm text-gray-600">{p.pain}</td>
                    <td className="px-4 py-3 text-sm text-gray-800 bg-emerald-50/50 font-medium">{p.fix}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* FEATURES */}
      <section className="py-16 bg-white" data-testid="features-section">
        <div className="max-w-4xl mx-auto px-4">
          <h2 className="text-base sm:text-lg font-bold text-gray-900 text-center mb-12">
            So löst ivoliq die E-Rechnung für dich
          </h2>
          <div className="grid md:grid-cols-2 gap-8">
            {features.map((f) => (
              <div key={f.title} className="flex gap-4">
                <div className="w-11 h-11 rounded-lg bg-emerald-100 text-emerald-700 flex items-center justify-center flex-shrink-0">
                  <f.icon className="h-5 w-5" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900 mb-1">{f.title}</h3>
                  <p className="text-sm text-gray-600 leading-relaxed">{f.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-16 bg-gray-50" data-testid="faq-section">
        <div className="max-w-3xl mx-auto px-4">
          <h2 className="text-base sm:text-lg font-bold text-gray-900 text-center mb-8">
            Häufige Fragen zur E-Rechnung
          </h2>
          <div className="bg-white rounded-xl border shadow-sm px-6">
            {faqItems.map((item) => (
              <FAQItem key={item.q} question={item.q} answer={item.a} />
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-emerald-600" data-testid="cta-section">
        <div className="max-w-3xl mx-auto px-4 text-center">
          <h2 className="text-base sm:text-lg font-bold text-white mb-4">
            E-Rechnungs-ready in 5 Minuten
          </h2>
          <p className="text-emerald-50 mb-8 text-sm leading-relaxed">
            Rechnung schreiben wie gewohnt — ZUGFeRD, XRechnung und der Empfang laufen automatisch mit.
          </p>
          <Button size="lg" onClick={() => navigate("/signup")} className="bg-white text-emerald-700 hover:bg-emerald-50 px-8" data-testid="cta-register">
            Kostenlos starten <ArrowRight className="h-5 w-5 ml-2" />
          </Button>
        </div>
      </section>

      <Footer />
    </div>
  );
}
